import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Copy, ExternalLink, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, mediaUrl } from "@/lib/api";
import { coverOf } from "@/lib/format";
import { Btn, PageHead } from "@/admin/AdminLayout";

const STATUSES = ["available", "reserved", "sold", "off_market", "coming_soon"];

export default function VehiclesAdmin() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-vehicles"], queryFn: async () => (await api.get("/admin/vehicles")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-vehicles"] }); qc.invalidateQueries({ queryKey: ["vehicles"] }); qc.invalidateQueries({ queryKey: ["featured"] }); };
  const patch = useMutation({ mutationFn: ({ id, body }) => api.patch(`/admin/vehicles/${id}`, body), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  const remove = useMutation({ mutationFn: (id) => api.delete(`/admin/vehicles/${id}`), onSuccess: () => { toast.success("Vehicle deleted"); refresh(); } });
  const duplicate = useMutation({ mutationFn: (id) => api.post(`/admin/vehicles/${id}/duplicate`), onSuccess: () => { toast.success("Listing duplicated (unpublished)"); refresh(); } });
  const reorder = useMutation({ mutationFn: (ids) => api.post("/admin/vehicles/reorder", { ids }), onSuccess: refresh });
  const move = (i, dir) => {
    const ids = data.map((v) => v.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate(ids);
  };
  return (
    <div data-testid="admin-vehicles">
      <PageHead title="Vehicles">
        <Link to="/admin/vehicles/new" className="btn btn-primary !py-2 !px-3.5 !text-[0.62rem]" data-testid="admin-vehicle-add">ADD VEHICLE</Link>
      </PageHead>
      <div className="space-y-2">
        {data.map((v, i) => {
          const c = coverOf(v);
          return (
            <div key={v.id} className="admin-card flex flex-wrap items-center gap-4 p-3" data-testid={`admin-vehicle-row-${v.slug}`}>
              <div className="h-14 w-20 shrink-0 overflow-hidden bg-[#0e1013]">{c.thumb && <img src={mediaUrl(c.thumb)} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{v.make} {v.model} <span className="text-[#8b95a5]">{v.version}</span></p>
                <p className="mono mt-1 text-[0.55rem] text-[#8b95a5]">{v.year} · {v.location} · #{v.sort_order}{v.featured ? " · FEATURED" : ""}</p>
              </div>
              <select className="field !w-auto" value={v.status} onChange={(e) => patch.mutate({ id: v.id, body: { status: e.target.value } })} data-testid={`admin-vehicle-status-${v.slug}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <Btn variant={v.published ? "primary" : "ghost"} onClick={() => patch.mutate({ id: v.id, body: { published: !v.published } })} data-testid={`admin-vehicle-publish-${v.slug}`}>{v.published ? "PUBLISHED" : "DRAFT"}</Btn>
              <Btn variant="ghost" onClick={() => patch.mutate({ id: v.id, body: { featured: !v.featured } })} data-testid={`admin-vehicle-feature-${v.slug}`}>{v.featured ? "★" : "☆"}</Btn>
              <div className="flex items-center gap-1 text-[#8b95a5]">
                <button onClick={() => move(i, -1)} className="p-1.5 hover:text-white" aria-label="Move up"><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} className="p-1.5 hover:text-white" aria-label="Move down"><ArrowDown size={14} /></button>
                <Link to={`/admin/vehicles/${v.id}`} className="p-1.5 hover:text-white" data-testid={`admin-vehicle-edit-${v.slug}`}><Pencil size={14} /></Link>
                <a href={`/vehicles/${v.slug}`} target="_blank" rel="noreferrer" className="p-1.5 hover:text-white" title="Preview"><ExternalLink size={14} /></a>
                <button onClick={() => duplicate.mutate(v.id)} className="p-1.5 hover:text-white" title="Duplicate" data-testid={`admin-vehicle-duplicate-${v.slug}`}><Copy size={14} /></button>
                <button onClick={() => window.confirm("Delete this vehicle?") && remove.mutate(v.id)} className="p-1.5 hover:text-red-400" data-testid={`admin-vehicle-delete-${v.slug}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
        {!data.length && <p className="text-sm text-[#8b95a5]">No vehicles yet.</p>}
      </div>
    </div>
  );
}
